import {
  ArrowLeft,
  ArrowRight,
  AtSign,
  ExternalLink,
  Info,
  LoaderCircle,
  MapPin,
  Phone,
  Scale,
  Search,
  UserRoundSearch,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CopyableText, useRouter, useToast } from "@tribunalea/ui";
import { CivitaAssistantLauncher } from "../components/CivitaAssistant.jsx";

const circumscriptionOptions = Object.freeze([
  {
    id: "1",
    ordinal: "Primera",
    area: "Capital · Godoy Cruz · Guaymallén · Las Heras · Lavalle · Luján de Cuyo · Maipú",
  },
  {
    id: "2",
    ordinal: "Segunda",
    area: "San Rafael · General Alvear · Malargüe",
  },
  {
    id: "3",
    ordinal: "Tercera",
    area: "San Martín · Junín · Rivadavia · Santa Rosa · La Paz",
  },
  {
    id: "4",
    ordinal: "Cuarta",
    area: "Tunuyán · Tupungato · San Carlos",
  },
]);

const SESSION_KEY = "civita:lawyer-directory-session";
const DIRECTORY_PATH = "/encontrar-abogado";

function directoryStateFromSearch(search) {
  const params = new URLSearchParams(search);
  const circumscription = params.get("circunscripcion") || "";
  const validCircumscription = circumscriptionOptions.some(
    ({ id }) => id === circumscription,
  );
  if (!validCircumscription) {
    return {
      circumscription: "",
      filters: { name: "", registration: "" },
      page: 1,
    };
  }

  const parsedPage = Number.parseInt(params.get("pagina") || "1", 10);
  return {
    circumscription,
    filters: {
      name: (params.get("nombre") || "").trim(),
      registration: (params.get("matricula") || "").trim(),
    },
    page: Number.isSafeInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1,
  };
}

function buildDirectoryUrl({ circumscription, filters, page = 1 }) {
  if (!circumscription) return DIRECTORY_PATH;
  const params = new URLSearchParams({ circunscripcion: circumscription });
  if (filters.name) params.set("nombre", filters.name);
  if (filters.registration) params.set("matricula", filters.registration);
  if (page > 1) params.set("pagina", String(page));
  return `${DIRECTORY_PATH}?${params.toString()}`;
}

function getDirectorySessionId() {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const value = window.crypto?.randomUUID?.()
      || `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem(SESSION_KEY, value);
    return value;
  } catch {
    return `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function initials(fullName) {
  return String(fullName || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toLocaleUpperCase("es-AR");
}

function LawyerCard({ lawyer }) {
  const address = [lawyer.address, lawyer.location].filter(Boolean).join(", ");
  return (
    <article className="lawyer-card">
      <header>
        <span className="lawyer-card__avatar" aria-hidden="true">
          {initials(lawyer.fullName)}
        </span>
        <div>
          <h3>
            <CopyableText
              copiedMessage="Nombre copiado"
              label="nombre"
              value={lawyer.fullName}
            />
          </h3>
          <p>
            Matrícula{" "}
            <CopyableText
              copiedMessage="Matrícula copiada"
              label="matrícula"
              value={lawyer.registrationNumber}
            />
          </p>
        </div>
      </header>
      <div className="lawyer-card__details">
        {lawyer.phone && (
          <p>
            <Phone size={15} />
            <CopyableText
              copiedMessage="Teléfono copiado"
              label="teléfono"
              value={lawyer.phone}
            />
          </p>
        )}
        {lawyer.email && (
          <p>
            <AtSign size={15} />
            <CopyableText
              copiedMessage="Correo copiado"
              label="correo"
              value={lawyer.email}
            />
          </p>
        )}
        {address && (
          <p>
            <MapPin size={15} />
            <CopyableText
              copiedMessage="Dirección copiada"
              label="dirección"
              value={address}
            />
          </p>
        )}
        {!lawyer.phone && !lawyer.email && !lawyer.address && !lawyer.location && (
          <p className="lawyer-card__empty">El padrón no publica datos de contacto.</p>
        )}
      </div>
    </article>
  );
}

export function FindLawyerPage({ api }) {
  const { showToast } = useToast();
  const { navigate, search } = useRouter();
  const urlState = useMemo(() => directoryStateFromSearch(search), [search]);
  const sessionIdRef = useRef(null);
  const requestIdRef = useRef(0);
  const resultsRef = useRef(null);
  const [name, setName] = useState(urlState.filters.name);
  const [registration, setRegistration] = useState(
    urlState.filters.registration,
  );
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const { circumscription, filters: appliedFilters, page } = urlState;

  useEffect(() => {
    setName(appliedFilters.name);
    setRegistration(appliedFilters.registration);

    if (!circumscription) {
      requestIdRef.current += 1;
      setResult(null);
      setLoading(false);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    let active = true;

    async function load() {
      try {
        if (!sessionIdRef.current) {
          sessionIdRef.current = getDirectorySessionId();
        }
        const data = await api.listLawyers({
          circumscription,
          name: appliedFilters.name,
          registration: appliedFilters.registration,
          page,
          sessionId: sessionIdRef.current,
        });
        if (active && requestId === requestIdRef.current) setResult(data);
      } catch (error) {
        if (active && requestId === requestIdRef.current) {
          setResult(null);
          showToast({ message: error.message, tone: "danger" });
        }
      } finally {
        if (active && requestId === requestIdRef.current) setLoading(false);
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [
    api,
    appliedFilters.name,
    appliedFilters.registration,
    circumscription,
    page,
    reloadKey,
    showToast,
  ]);

  function goToDirectory(nextState) {
    const target = buildDirectoryUrl(nextState);
    const current = `${DIRECTORY_PATH}${search}`;
    if (target === current) {
      setReloadKey((value) => value + 1);
      return;
    }
    navigate(target, { scroll: false });
  }

  function selectCircumscription(id) {
    goToDirectory({
      circumscription: id,
      filters: { name: "", registration: "" },
      page: 1,
    });
  }

  function submitSearch(event) {
    event.preventDefault();
    if (!circumscription || loading) return;
    const filters = {
      name: name.trim(),
      registration: registration.trim(),
    };
    goToDirectory({
      circumscription,
      filters,
      page: 1,
    });
  }

  function changePage(page) {
    if (!circumscription || loading) return;
    goToDirectory({
      circumscription,
      filters: appliedFilters,
      page,
    });
    window.requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  const selectedOption = circumscriptionOptions.find(
    ({ id }) => id === circumscription,
  );
  const officialDirectoryUrl = result?.items.find(({ directoryUrl: url }) => url)
    ?.directoryUrl;

  return (
    <main className="civita-document-page lawyer-directory-page">
      <header className="lawyer-directory-heading">
        <span className="eyebrow">Padrón de Mendoza</span>
        <h1>Encontrá un abogado</h1>
        <p>
          Consultá profesionales matriculados por circunscripción judicial y
          buscá por nombre, matrícula o ambos datos.
        </p>
      </header>

      {!circumscription ? (
        <section className="circumscription-picker" aria-labelledby="circumscription-title">
          <header>
            <span>1</span>
            <div>
              <h2 id="circumscription-title">Elegí la circunscripción</h2>
              <p>Es necesaria para consultar el colegio profesional correcto.</p>
            </div>
          </header>
          <div className="circumscription-grid">
            {circumscriptionOptions.map((option) => (
              <button
                type="button"
                key={option.id}
                onClick={() => selectCircumscription(option.id)}
              >
                <span>{option.id}</span>
                <div>
                  <strong>{option.ordinal} Circunscripción</strong>
                  <small>{option.area}</small>
                </div>
                <ArrowRight size={18} />
              </button>
            ))}
          </div>
        </section>
      ) : (
        <>
          <section className="lawyer-directory-controls">
            <div className="selected-circumscription">
              <span><Scale size={19} /></span>
              <div>
                <small>Circunscripción elegida</small>
                <strong>{selectedOption.ordinal} Circunscripción · {selectedOption.area}</strong>
              </div>
              <button type="button" onClick={() => goToDirectory({
                circumscription: "",
                filters: { name: "", registration: "" },
                page: 1,
              })}>
                Cambiar
              </button>
            </div>

            <form className="lawyer-search-form" onSubmit={submitSearch}>
              <label>
                <span>Nombre</span>
                <input
                  type="search"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Ej.: María González"
                  maxLength={150}
                />
              </label>
              <label>
                <span>Matrícula</span>
                <input
                  type="search"
                  inputMode="numeric"
                  value={registration}
                  onChange={(event) => setRegistration(event.target.value)}
                  placeholder="Ej.: 1000"
                  maxLength={40}
                />
              </label>
              <button type="submit" disabled={loading}>
                {loading ? <LoaderCircle className="is-spinning" size={17} /> : <Search size={17} />}
                Buscar
              </button>
            </form>
          </section>

          <aside className="lawyer-random-note">
            <Info size={18} />
            <p>
              <strong>El orden es aleatorio para esta sesión.</strong>{" "}
              Así no damos preferencia a ningún profesional. Al cambiar de página,
              el orden se mantiene.
            </p>
          </aside>

          {loading && !result ? (
            <section className="lawyer-loading" aria-live="polite">
              <LoaderCircle className="is-spinning" size={25} />
              <p>Consultando el padrón indexado…</p>
            </section>
          ) : result && (
            <section
              className="lawyer-results"
              aria-live="polite"
              ref={resultsRef}
            >
              <header>
                <div>
                  <span className="eyebrow">Resultados</span>
                  <h2>
                    {result.totalItems
                      ? `${result.totalItems} ${result.totalItems === 1 ? "profesional" : "profesionales"}`
                      : "Sin coincidencias"}
                  </h2>
                </div>
                {result.totalPages > 0 && (
                  <p>Página {result.page} de {result.totalPages}</p>
                )}
              </header>

              {result.items.length ? (
                <div className={`lawyer-card-grid ${loading ? "is-loading" : ""}`}>
                  {result.items.map((lawyer) => (
                    <LawyerCard
                      lawyer={lawyer}
                      key={`${lawyer.registrationNumber}:${lawyer.fullName}`}
                    />
                  ))}
                </div>
              ) : (
                <div className="lawyer-empty-state">
                  <UserRoundSearch size={29} />
                  <h3>No encontramos profesionales con esos datos</h3>
                  <p>Probá con una parte del nombre, revisá la matrícula o dejá ambos campos vacíos para ver el padrón.</p>
                </div>
              )}

              {result.totalPages > 1 && (
                <nav className="lawyer-pagination" aria-label="Páginas del padrón">
                  <button
                    type="button"
                    disabled={!result.hasPreviousPage || loading}
                    onClick={() => changePage(result.page - 1)}
                  >
                    <ArrowLeft size={16} /> Anterior
                  </button>
                  <span>{result.page} / {result.totalPages}</span>
                  <button
                    type="button"
                    disabled={!result.hasNextPage || loading}
                    onClick={() => changePage(result.page + 1)}
                  >
                    Siguiente <ArrowRight size={16} />
                  </button>
                </nav>
              )}

              <footer className="lawyer-source-note">
                <p>
                  La información es una copia periódica del padrón del colegio.
                  La inclusión no implica una recomendación de Tribunalea.
                  Confirmá que la matrícula y los datos sigan vigentes antes de
                  contratar.
                </p>
                {officialDirectoryUrl && (
                  <a href={officialDirectoryUrl} target="_blank" rel="noreferrer">
                    Ver padrón oficial <ExternalLink size={14} />
                  </a>
                )}
              </footer>
            </section>
          )}
        </>
      )}

      <CivitaAssistantLauncher
        className="lawyer-directory-assistant"
        title="También podés buscar con Justiniano"
        description={circumscription
          ? `Ya sabe que elegiste la ${selectedOption.ordinal.toLocaleLowerCase("es-AR")} circunscripción. Pedile que busque por nombre o matrícula.`
          : "Decile primero la circunscripción y después pedile que liste o busque por nombre o matrícula."}
        conversationScope="encontrar-abogado:v1"
        conversationMetadata={{ surface: "civita", route: "/encontrar-abogado" }}
        welcome={circumscription
          ? `Elegiste la ${selectedOption.ordinal} Circunscripción de Mendoza. Puedo listar el padrón o buscar por nombre y matrícula.`
          : "Para buscar un abogado, primero necesito saber si corresponde la circunscripción 1, 2, 3 o 4 de Mendoza."}
        suggestions={circumscription
          ? ["Mostrame abogados", "Quiero buscar por nombre", "Quiero buscar por matrícula"]
          : ["¿Cómo sé cuál es mi circunscripción?", "Quiero buscar en la circunscripción 1", "Quiero buscar en la circunscripción 2"]}
        appContext={{
          route: "/encontrar-abogado",
          activeEntity: null,
          lawyerDirectory: circumscription
            ? { circumscription }
            : null,
        }}
      />
    </main>
  );
}
